import React, {
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
} from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { Col, Row } from "react-bootstrap";
import AddNote from "./AddNote/AddNote";

import CSVExport from "../../../Common/ExportRecords/CSVExport";
import { changeDateAndAddTime } from "../../../VendorOnboarding/Views/VendorProfile/helpers";

import * as Constants from "../../../../constants/Constants";
import * as RolePermissionConstants from "../../../../constants/RolePermissionConstants";
import { getUserRolePermission } from "../../../../utils/rolePermissionAccess";
import {
  httpGetThreeNotesByProfileId,
  httpPostCreateOrUpdateNote,
  httpGetNoteByVendorId,
  httpDeleteNotes,
} from "../../../../service/httpClient";
import { updateServiceData } from "../../../../redux/serviceSlice";

import "./notes.css";

const dateComparator = (date1, date2) => {
  let date1Number = Date.parse(date1);
  let date2Number = Date.parse(date2);
  if (
    (isNaN(date1Number) || date1Number === null) &&
    (isNaN(date2Number) || date2Number === null)
  ) {
    return 0;
  }
  if (isNaN(date1Number) || date1Number === null) {
    return -1;
  }
  if (isNaN(date2Number) || date2Number === null) {
    return 1;
  }
  return date1Number - date2Number;
};

const Notes = (props, ref) => {
  let gridApi = null;
  const gridConfig = {
    columnDefs: [
      {
        field: "createdDate",
        headerName: "Date",
        sortable: true,
        sort: "desc",
        sortingOrder: ["asc", "desc"],
        width: 150,
        filter: true,
        resizable: true,
        lockVisible: true,
        suppressMovable: true,
        comparator: dateComparator,
        cellStyle: { "white-space": "normal" },
      },
      {
        field: "updatedByFullName",
        headerName: "User",
        sortable: true,
        sortingOrder: ["asc", "desc"],
        filter: true,
        resizable: true,
        lockVisible: true,
        suppressMovable: true,
        width: 120,
        cellStyle: { "white-space": "normal" },
      },
      {
        field: "noteType",
        headerName: "Note Type",
        sortable: true,
        sortingOrder: ["asc", "desc"],
        filter: true,
        resizable: true,
        lockVisible: true,
        suppressMovable: true,
        width: 150,
        cellStyle: { "white-space": "normal" },
      },
      {
        field: "notes",
        headerName: "Note",
        sortable: true,
        sortingOrder: ["asc", "desc"],
        filter: true,
        resizable: true,
        lockVisible: true,
        suppressMovable: true,
        width: 300,
        cellStyle: { "white-space": "normal" },
      },
      { field: "id", hide: true },
      { field: "duns", hide: true },
    ],
    overlayNoRowsTemplate: "No Notes Updated Yet",
    rowHeight: 75,
    rowSelection: "multiple",
  };

  const [rowData, setRowData] = useState([]);

  const dispatch = useDispatch();
  const store = useSelector(
    ({ service, common }) => ({
      profileId: common.profileId,
      duns: common.duns,
      vendorCorpName: common.vendorCorpName,
      merchandiseType:
        service.vendorProfile?.vendorDetail?.merchandiseType || "",
    }),
    shallowEqual
  );

  useEffect(() => {
    getNotes();
  }, []);

  useImperativeHandle(ref, () => ({
    refreshNotes: () => getNotes(),
  }));

  const onNotesUpdated = async () => {
    try {
      const res = await httpGetThreeNotesByProfileId(store.profileId);
      if (res.status === 200 && res.data) {
        dispatch(updateServiceData({ notes: res.data }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onRemoveNote = async (noteId) => {
    try {
      const res = await httpDeleteNotes(noteId);
      if (res.status === 200) {
        getNotes();
        onNotesUpdated();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getNotes = async () => {
    try {
      const res = await httpGetNoteByVendorId(store.profileId);
      if (res.status === 200 && res.data) {
        setRowData(res.data);
      } else {
        setRowData([]);
      }
    } catch (error) {
      console.log(error);
      setRowData([]);
    }
  };

  const addNoteHandler = async (_notes) => {
    try {
      let notes = { ..._notes };
      notes.createdDate = changeDateAndAddTime(
        new Date(_notes.createdDate)
          .toJSON()
          .slice(0, 10)
          .replace(/-/g, "-")
      );

      const res = await httpPostCreateOrUpdateNote(notes);

      if (res.status === 200 && res.data) {
        setRowData(res.data);
        onNotesUpdated();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onGridReady = (params) => {
    gridApi = params.api;
    gridApi.sizeColumnsToFit();
  };

  return (
    <>
      <Row>
        <Col xs={12} style={{ textAlign: "end" }}>
          <CSVExport
            rowData={rowData}
            header={Constants.NOTEREPORTHEADERS}
            filename={`Vendor_${store.duns}_Notes.csv`}
            classSelected="MuiButtonBase-root"
            disableExport={
              !(
                getUserRolePermission(
                  RolePermissionConstants.VENDORPLATFORM_NOTES_NOTELIST,
                  RolePermissionConstants.VMP_EXPORT
                ) === RolePermissionConstants.EDIT
              )
            }
            label="Export Notes"
            labelclassName="MuiButton-label"
          />
          <AddNote
            addNote={addNoteHandler}
            vendorName={store.vendorCorpName}
            vendorId={store.profileId}
            vendorDuns={store.duns}
            showTriggerButton={true}
            merchandiseType={store.merchandiseType}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="ag-theme-balham table">
            <AgGridReact
              columnDefs={gridConfig.columnDefs}
              rowData={rowData}
              onGridReady={onGridReady}
              context={{
                componentParent: {
                  onRemoveNote,
                },
              }}
              rowHeight={gridConfig.rowHeight}
              overlayNoRowsTemplate={gridConfig.overlayNoRowsTemplate}
              rowSelection={gridConfig.rowSelection}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default forwardRef(Notes);
